import {
  defaultConfiguration,
  sameSettingValue,
  valueStatedUnder,
} from "@thalamos/haven-common";
import type {
  SettingKey,
  SettingValueJson,
  SettingView,
} from "@thalamos/haven-common";

/**
 * A configuration's settings as the server resolved them, or null for no
 * configuration, which leaves every setting at the product's default.
 */
export type ResolvedSettings = readonly SettingView[] | null;

/**
 * What a setting comes to for a holder under `parent`, with what the holder
 * states for itself applied over it.
 */
const valueUnder = ({
  parent,
  key,
  stated,
}: {
  parent: ResolvedSettings;
  key: SettingKey;
  stated: SettingValueJson | null;
}) => {
  const given = parent?.find((setting) => setting.key === key);
  const inherited = given?.value ?? defaultConfiguration[key];

  if (stated === null) {
    return inherited;
  }

  // A statement under a default sets the value outright, as it does at the top
  // of a chain.
  return given !== undefined && given.source.kind !== "default"
    ? valueStatedUnder({ key, inherited, stated })
    : stated;
};

/**
 * The settings that apply differently under `to` than under `from`, for an
 * organisation moving from one configuration to another.
 *
 * `organisationSettings` are its own, whose statements apply over either
 * configuration; null compares the configurations alone.
 */
export const settingsDifferingBetween = ({
  from,
  to,
  organisationSettings,
}: {
  from: ResolvedSettings;
  to: ResolvedSettings;
  organisationSettings: readonly SettingView[] | null;
}): SettingKey[] =>
  (from ?? to ?? [])
    .map((setting) => setting.key)
    .filter((key) => {
      const stated =
        organisationSettings?.find((setting) => setting.key === key)?.stated ??
        null;

      return !sameSettingValue(
        valueUnder({ parent: from, key, stated }),
        valueUnder({ parent: to, key, stated }),
      );
    });

/**
 * The settings a configuration would get differently if it sat under `parent`
 * instead of where it sits now; a null parent puts it at the top of a chain.
 *
 * What it states itself still applies over the new parent, narrowed where the
 * setting narrows. Compared before two settings are reconciled against each
 * other, so a move that changes only how they are reconciled is not named.
 */
export const settingsChangedByMoving = ({
  configuration,
  parent,
}: {
  configuration: readonly SettingView[];
  parent: ResolvedSettings;
}): SettingKey[] =>
  configuration
    .filter(
      (setting) =>
        !sameSettingValue(
          valueUnder({
            parent,
            key: setting.key,
            stated: setting.stated,
          }),
          setting.reconciledFrom ?? setting.value,
        ),
    )
    .map((setting) => setting.key);
